import type { ExerciseType } from "./types";

export type PerformedSet = {
  weight: number | null;
  reps: number | null;
  durationSec: number | null;
};

export type SuggestionKind =
  | "first"
  | "increase"
  | "add-load"
  | "reps-up"
  | "repeat"
  | "deload"
  | "time-up"
  | "time-max"
  | "cardio-up"
  | "cardio-keep";

export type Suggestion = {
  kind: SuggestionKind;
  /** Suggested working weight in kg (null = choose yourself / bodyweight). */
  weight: number | null;
  /** Reps, seconds or minutes depending on the exercise type. */
  target: number;
  /** Weight change vs. last time, in kg. */
  delta: number;
};

/** Pause after which the plan says to restart with ~10 % less weight. */
export const DELOAD_AFTER_DAYS = 14;

export function roundWeight(kg: number, step = 0.5): number {
  return Math.round(kg / step) * step;
}

/** Epley estimate of the one-rep max. */
export function estimateOneRepMax(weight: number, reps: number): number {
  if (reps <= 0 || weight <= 0) return 0;
  if (reps === 1) return weight;
  return weight * (1 + reps / 30);
}

/**
 * Double-progression rule from the plan: once every working set reaches the
 * top of the rep range, add weight (+weightStep); otherwise keep the weight
 * and aim for one more rep.
 */
export function suggestNext(opts: {
  type: ExerciseType;
  targetMin: number;
  targetMax: number;
  sets: number;
  weightStep: number;
  lastSets: PerformedSet[];
  daysSinceLast: number | null;
}): Suggestion {
  const { type, targetMin, targetMax, sets, weightStep, lastSets, daysSinceLast } = opts;

  if (lastSets.length === 0) {
    return { kind: "first", weight: null, target: targetMin, delta: 0 };
  }

  const lastWeight = Math.max(0, ...lastSets.map((s) => s.weight ?? 0));
  const longBreak = daysSinceLast != null && daysSinceLast >= DELOAD_AFTER_DAYS;

  if (type === "cardio") {
    const minutes = Math.max(...lastSets.map((s) => Math.round((s.durationSec ?? 0) / 60)));
    if (longBreak || minutes <= 0) {
      return { kind: "cardio-keep", weight: null, target: targetMin, delta: 0 };
    }
    if (minutes < targetMax) {
      return {
        kind: "cardio-up",
        weight: null,
        target: Math.min(targetMax, Math.max(targetMin, Math.ceil(minutes * 1.1))),
        delta: 0,
      };
    }
    return { kind: "cardio-keep", weight: null, target: minutes, delta: 0 };
  }

  if (type === "time") {
    const durations = lastSets.map((s) => s.durationSec ?? 0);
    const shortest = Math.min(...durations);
    if (longBreak) return { kind: "deload", weight: null, target: targetMin, delta: 0 };
    if (lastSets.length >= sets && shortest >= targetMax) {
      return { kind: "time-max", weight: null, target: targetMax, delta: 0 };
    }
    return {
      kind: "time-up",
      weight: null,
      target: Math.min(targetMax, Math.max(targetMin, shortest + 5)),
      delta: 0,
    };
  }

  if (longBreak && lastWeight > 0) {
    const weight = roundWeight(lastWeight * 0.9);
    return { kind: "deload", weight, target: targetMin, delta: weight - lastWeight };
  }

  const reps = lastSets.map((s) => s.reps ?? 0);
  const fewest = Math.min(...reps);
  const allAtTop = lastSets.length >= sets && fewest >= targetMax;

  if (allAtTop) {
    if (lastWeight <= 0) {
      const step = weightStep > 0 ? weightStep : 2.5;
      return { kind: "add-load", weight: step, target: targetMin, delta: step };
    }
    if (weightStep > 0) {
      return {
        kind: "increase",
        weight: lastWeight + weightStep,
        target: targetMin,
        delta: weightStep,
      };
    }
  }

  if (fewest >= targetMin) {
    return {
      kind: "reps-up",
      weight: lastWeight || null,
      target: Math.min(targetMax, fewest + 1),
      delta: 0,
    };
  }

  return { kind: "repeat", weight: lastWeight || null, target: targetMin, delta: 0 };
}
